import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Loader2, Sprout, ChevronRight, Calendar, MapPin } from "lucide-react";
import { TopBar, BottomNav } from "../components/Nav";
import { API } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";
import { toast } from "sonner";

const STAGE_BADGE = {
  seedling: "bg-[#D4A373]/30 text-[#1C1C1C]",
  vegetative: "bg-[#52796F]/30 text-[#1B4332]",
  flowering: "bg-[#E07A5F]/30 text-[#1C1C1C]",
  fruiting: "bg-[#E9C46A]/40 text-[#1C1C1C]",
  harvest: "bg-[#2B9348]/25 text-[#1B4332]",
};

export default function FieldDetailPage() {
  const { id } = useParams();
  const { t } = useLang();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/fields/${id}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Field not found"))
      .finally(() => setLoading(false));
  }, [id]);

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-24 md:pb-10">
      <TopBar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        <Link to="/fields" data-testid="back-to-fields" className="flex items-center gap-1 text-[#1B4332] font-semibold">
          <ArrowLeft className="w-4 h-4" /> {t("common.back")}
        </Link>

        {loading && (
          <div className="flex justify-center py-20" data-testid="field-loading">
            <Loader2 className="w-6 h-6 animate-spin text-[#52796F]" />
          </div>
        )}

        {!loading && data && (
          <>
            <div className="bg-white border border-[#E5E0D8] rounded-3xl p-6" data-testid="field-header">
              <h1 className="cd-heading text-3xl font-extrabold">{data.field.name}</h1>
              <div className="flex flex-wrap gap-3 mt-2 text-sm text-[#52796F]">
                {data.field.crop_name && (
                  <span className="flex items-center gap-1">
                    <Sprout className="w-4 h-4" /> {data.field.crop_name}
                  </span>
                )}
                {data.field.planted_on && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" /> {new Date(data.field.planted_on).toLocaleDateString()}
                  </span>
                )}
                {data.field.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" /> {data.field.location}
                  </span>
                )}
              </div>
            </div>

            <div>
              <h2 className="cd-heading text-xl font-bold mt-2 mb-3">{t("fields.timeline")}</h2>
              {data.scans.length === 0 ? (
                <div
                  className="bg-white border border-[#E5E0D8] rounded-2xl p-8 text-center text-sm text-[#52796F]"
                  data-testid="timeline-empty"
                >
                  {t("fields.noTimelineScans")}
                </div>
              ) : (
                <ol className="relative border-l-2 border-[#E5E0D8] ml-3 space-y-5" data-testid="timeline-list">
                  {data.scans.map((s) => (
                    <li
                      key={s.id}
                      data-testid={`timeline-item-${s.id}`}
                      onClick={() => nav(`/scan/${s.id}`)}
                      className="ml-4 cursor-pointer"
                    >
                      <span className="absolute -left-[9px] w-4 h-4 rounded-full bg-[#1B4332] border-4 border-[#FDFBF7]" />
                      <div className="bg-white rounded-2xl border border-[#E5E0D8] p-4 cd-lift flex gap-3">
                        <img
                          src={s.thumbnail || s.image_base64}
                          alt="scan"
                          className="w-20 h-20 object-cover rounded-xl"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-[#52796F]">
                            {new Date(s.created_at).toLocaleString()}
                          </div>
                          <div className="font-bold mt-0.5">
                            {s.result?.healthy
                              ? t("diag.healthy")
                              : s.result?.disease_name || s.result?.crop_name || t("diag.unknown")}
                          </div>
                          <div className="flex flex-wrap items-center gap-2 mt-1.5 text-xs">
                            {s.result?.growth_stage && (
                              <span className={`px-2 py-0.5 rounded-full font-semibold ${STAGE_BADGE[s.result.growth_stage] || "bg-[#F4F1EA]"}`}>
                                {s.result.growth_stage}
                              </span>
                            )}
                            <span className="text-[#52796F]">{s.result?.confidence || 0}%</span>
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-[#52796F] self-center" />
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </>
        )}
      </main>
      <BottomNav />
    </div>
  );
}
